import { useState } from "react";
import { Pin, Workflow } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { useFetcher } from "~/hooks/useFetcher";
import { ConversationProvider } from "~/pages/manifold-nextgen/components/conversations/ConversationContext";
import { ArtifactPreviewBody } from "~/pages/manifold-nextgen/components/conversations/artifacts/ArtifactPreviewBody";
import type { ArtifactPreview } from "~/services/artifacts";
import { PinnedArtifact, pinnedArtifactPreviewPath } from "~/services/pinnedArtifacts";
import { PinnedArtifactWorkflow } from "./PinnedArtifactWorkflow";

interface PinnedArtifactCardProps {
  pin: PinnedArtifact;
  canCurate: boolean;
  onUnpin: () => void;
}

// One pinned artifact on the project artifacts page.
//
// The preview resolves through the *pin*, not through an artifact id, which is
// what lets a member render a card someone else pinned and what makes the card
// follow a refresh on its own — the pin moves, the next read renders the new
// artifact, and nothing here has to be invalidated.
export const PinnedArtifactCard = ({ pin, canCurate, onUnpin }: PinnedArtifactCardProps) => {
  const [workflowOpen, setWorkflowOpen] = useState(false);
  const { data, error, isLoading } = useFetcher<ArtifactPreview>(
    pinnedArtifactPreviewPath(pin.project_id, pin.id)
  );

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-background/60">
      <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/40 px-3 py-2">
        <span className="truncate text-sm font-medium" title={pin.display_name}>
          {pin.display_name}
        </span>
        <div className="flex shrink-0 items-center gap-0.5">
          {/* Open to every member: refreshing is member-gated, and how the
              artifact was made is what the pin is claiming. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setWorkflowOpen(true)}
                aria-label="View workflow"
                className="h-7 w-7 p-0 text-muted-foreground"
              >
                <Workflow className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>View workflow</TooltipContent>
          </Tooltip>
          {canCurate && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onUnpin}
                  aria-label="Unpin from project"
                  className="h-7 w-7 p-0 text-muted-foreground"
                >
                  <Pin className="size-3.5 fill-current" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Unpin from project</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
      <div className="px-3 py-2">
        {/* No conversation is in scope here: the artifact belongs to whoever
            pinned or last refreshed it. A null id keeps the renderers from
            offering conversation-scoped actions this reader cannot perform. */}
        <ConversationProvider conversationId={null}>
          <ArtifactPreviewBody
            preview={data}
            name={pin.display_name}
            error={error}
            isLoading={isLoading}
          />
        </ConversationProvider>
      </div>
      {workflowOpen && (
        <PinnedArtifactWorkflow
          pin={pin}
          open={workflowOpen}
          onClose={() => setWorkflowOpen(false)}
        />
      )}
    </div>
  );
};
